const DEFAULT_IDS = {
    selectId: "business_industry",
    segmentInputId: "business_segment",
    subsegmentInputId: "business_subsegment",
    customWrapperId: "custom-subsegment-wrapper",
    customInputId: "custom_subsegment",
};

const DEFAULT_MANAGER = {
    resolveInput(input) {
        return { targetInput: input, displayInput: input };
    },
    getDisplayInput() {
        return null;
    },
};

/**
 * Initializes shared behaviour for business segment selection fields.
 * Handles synchronization between the select element, hidden fields,
 * and optional custom subsegment input.
 *
 * @param {HTMLFormElement|null} form - The form containing the business fields.
 * @param {Object} [overrides={}] - Optional overrides for element IDs.
 * @returns {{resolveInput: function(HTMLElement): {targetInput: HTMLElement|null, displayInput: HTMLElement|null}}}
 */
export function initBusinessIndustryFields(form, overrides = {}) {
    if (!form) {
        return DEFAULT_MANAGER;
    }

    const config = { ...DEFAULT_IDS, ...overrides };

    const select = form.querySelector(`#${config.selectId}`);
    const segmentInput = form.querySelector(`#${config.segmentInputId}`);
    const subsegmentInput = form.querySelector(`#${config.subsegmentInputId}`);
    const customWrapper = form.querySelector(`#${config.customWrapperId}`);
    const customInput = form.querySelector(`#${config.customInputId}`);

    if (!select || !segmentInput || !subsegmentInput) {
        return DEFAULT_MANAGER;
    }

    const toggleCustomWrapper = (shouldShow) => {
        if (!customWrapper) return;

        if (shouldShow) {
            customWrapper.classList.remove("d-none");
        } else {
            customWrapper.classList.add("d-none");
        }
    };

    const setCustomSubsegmentRequired = (isRequired) => {
        if (!customInput) return;
        customInput.required = !!isRequired;
    };

    const handleCustomSubsegmentInput = () => {
        if (customInput && subsegmentInput) {
            subsegmentInput.value = customInput.value;
        }
    };

    const handleBusinessIndustryChange = () => {
        const selectedOption = select.options[select.selectedIndex];

        if (!selectedOption || selectedOption.value === "") {
            if (customWrapper && !customWrapper.classList.contains("d-none")) {
                setCustomSubsegmentRequired(true);
            }
            return;
        }

        const optgroup = selectedOption.parentElement;

        if (optgroup && optgroup.tagName === "OPTGROUP") {
            segmentInput.value = optgroup.label;

            if (selectedOption.value === "others") {
                toggleCustomWrapper(true);
                setCustomSubsegmentRequired(true);

                if (subsegmentInput.value === selectedOption.text) {
                    subsegmentInput.value = "";
                }
            } else {
                toggleCustomWrapper(false);
                setCustomSubsegmentRequired(false);
                subsegmentInput.value = selectedOption.text;
            }
        } else {
            segmentInput.value = selectedOption.text || selectedOption.value;
            subsegmentInput.value = selectedOption.text || selectedOption.value;
            toggleCustomWrapper(false);
            setCustomSubsegmentRequired(false);
        }
    };

    const ensureInitialState = () => {
        const selectedOption = select.options[select.selectedIndex];

        if (!selectedOption || selectedOption.value === "") {
            if (segmentInput.value && segmentInput.value.toLowerCase() === "others") {
                toggleCustomWrapper(true);
                setCustomSubsegmentRequired(true);
            }
        }
    };

    select.addEventListener("change", handleBusinessIndustryChange);
    customInput?.addEventListener("input", handleCustomSubsegmentInput);

    ensureInitialState();
    handleBusinessIndustryChange();

    if (customWrapper && !customWrapper.classList.contains("d-none")) {
        setCustomSubsegmentRequired(true);
        handleCustomSubsegmentInput();
    }

    return {
        resolveInput(input) {
            if (!input) {
                return { targetInput: null, displayInput: null };
            }

            if (customInput && input.id === customInput.id) {
                return { targetInput: subsegmentInput, displayInput: customInput };
            }

            return { targetInput: input, displayInput: input };
        },
        getDisplayInput(fieldName) {
            if (!fieldName) {
                return null;
            }

            if (fieldName === config.segmentInputId) {
                return select;
            }

            if (fieldName === config.subsegmentInputId) {
                if (customWrapper && customInput && !customWrapper.classList.contains("d-none")) {
                    return customInput;
                }

                return select;
            }

            return form.querySelector(`#${fieldName}`);
        },
    };
}
